// Maps a variant's "Color" option value (free text an admin typed, e.g. "Rose Gold",
// "Persian Blue", "Ruby+Green") to actual swatch color(s) for the small dots shown on
// product cards and the product page's picker.
const NAMED_COLORS = {
  gold: '#D4AF37',
  'rose gold': '#B76E79',
  'antique gold': '#C08A3E',
  'gold toned': '#D4AF37',
  'gold-toned': '#D4AF37',
  silver: '#C0C0C0',
  oxidised: '#8A8A8A',
  oxidized: '#8A8A8A',
  platinum: '#E5E4E2',
  bronze: '#8C6B3F',
  copper: '#B87333',
  champagne: '#F1DDBF',

  black: '#1F1F1F',
  charcoal: '#36454F',
  white: '#FFFFFF',
  ivory: '#FFF8E7',
  cream: '#FFF5DE',
  nude: '#E3BC9A',

  red: '#DC2626',
  maroon: '#7F1D1D',
  wine: '#722F37',
  burgundy: '#800020',
  ruby: '#9B111E',
  crimson: '#B91C3C',
  coral: '#FF7F50',
  rust: '#B7410E',

  pink: '#EC4899',
  'baby pink': '#F9A8D4',
  blush: '#FEC5CB',
  fuchsia: '#D6249F',
  magenta: '#D6249F',
  rose: '#E8A0BF',

  purple: '#9333EA',
  violet: '#7C3AED',
  lavender: '#C4B5FD',
  lilac: '#C8A2C8',
  plum: '#8E4585',

  blue: '#2563EB',
  'navy blue': '#1E3A8A',
  navy: '#1E3A8A',
  'sky blue': '#38BDF8',
  'persian blue': '#1C39BB',
  cobalt: '#0047AB',
  indigo: '#3F51B5',
  denim: '#1560BD',
  teal: '#0D9488',
  turquoise: '#14B8A6',
  aqua: '#22D3EE',
  cyan: '#06B6D4',

  green: '#16A34A',
  'emerald green': '#059669',
  emerald: '#50C878',
  olive: '#65A30D',
  mint: '#98FF98',
  sage: '#9CAF88',
  forest: '#228B22',

  yellow: '#EAB308',
  mustard: '#CA8A04',
  amber: '#F59E0B',
  lemon: '#FDE047',

  orange: '#EA580C',
  peach: '#FDBA74',
  apricot: '#FBCEB1',

  brown: '#78350F',
  tan: '#D2B48C',
  beige: '#E8DCC8',
  khaki: '#C3B091',
  coffee: '#6F4E37',
  chocolate: '#7B3F00',

  grey: '#6B7280',
  gray: '#6B7280',

  multicolor: '#F472B6',
  'multi color': '#F472B6',
  'multi-color': '#F472B6'
};

// Longest keys first, so "rose gold" matches before the bare "gold"/"rose" inside it.
const SORTED_KEYS = Object.keys(NAMED_COLORS).sort((a, b) => b.length - a.length);

// Cheap string hash -> HSL, only reached when nothing in the table matches even as a
// substring - gives an unrecognized name a stable, distinct-looking color instead of
// every unmapped value looking identical.
const fallbackColorFromText = (text) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 55%)`;
};

// Exact match first ("persian blue" -> its own entry); otherwise looks for any known
// color word contained IN the text ("Antique Gold Finish" -> matches "antique gold"),
// so slightly-decorated names still resolve correctly instead of falling through to a
// random fallback hue that has nothing to do with the actual color.
export const colorNameToHex = (name) => {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return '#D1D5DB'; // neutral grey for blank values
  if (NAMED_COLORS[key]) return NAMED_COLORS[key];
  const match = SORTED_KEYS.find((known) => key.includes(known));
  return match ? NAMED_COLORS[match] : fallbackColorFromText(key);
};

// Splits a value like "Ruby+Green" or "Ruby / Green" into its component color names,
// for two-tone (or more) products - each side of the delimiter becomes its own wedge
// in the dot. A plain "Blue" returns a single-element array (a normal solid dot).
export const splitColorValue = (name) =>
  String(name || '')
    .split(/[+/&]|(?:\s+and\s+)/i)
    .map((part) => part.trim())
    .filter(Boolean);

// The hex/CSS color(s) to actually paint a swatch with, in delimiter order.
export const colorValueToSwatchColors = (name) => splitColorValue(name).map(colorNameToHex);
